import { useLocation } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { PlatformSwitcher } from './PlatformSwitcher';
import { useAuth } from '../contexts/AuthContext';
import { useTenants } from '../hooks/useTenants';
import { useChatNotifications } from '../contexts/ChatNotificationsContext';
import { useMarketplaceConnections, type ExtensionConnection, type ExtensionHealthState, type ExtensionOverallState } from '../hooks/useMarketplaceConnections';
import { useCredits } from '../hooks/useCredits';
import { PlatformIcon } from './shared/PlatformIcon';

function healthTone(state: ExtensionHealthState): 'ok' | 'warn' | 'error' {
  if (state === 'ok') return 'ok';
  if (state === 'logged_out') return 'error';
  return 'warn';
}

function formatCheckedAt(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function describeExtensionIndicator(connections: ExtensionConnection[], overall: ExtensionOverallState) {
  const count = connections.length;
  if (count === 0) {
    return { tone: 'off' as const, label: 'Sem extensões', title: 'Nenhuma extensão conectada', dotClass: 'bg-slate-300', buttonClass: 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50' };
  }
  if (overall === 'ok') {
    return { tone: 'ok' as const, label: `${count} ${count === 1 ? 'extensão' : 'extensões'}`, title: `${count} extensão(ões) conectada(s), todos os acessos ok`, dotClass: 'bg-emerald-500', buttonClass: 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50' };
  }
  const problems = connections.filter((c) => c.health.state !== 'ok');
  const failingLabels = problems.flatMap((c) => c.health.checks.filter((k) => !k.ok).map((k) => k.label));
  const title = overall === 'logged_out'
    ? 'Extensão conectada, mas há conta deslogada no navegador'
    : overall === 'unknown'
      ? 'Extensão conectada, aguardando verificação dos acessos'
      : `Extensão conectada, mas sem acesso a: ${failingLabels.join(', ') || 'alguns recursos'}`;
  return { tone: 'warn' as const, label: `${count} ${count === 1 ? 'extensão' : 'extensões'} · atenção`, title, dotClass: 'bg-amber-500', buttonClass: 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100' };
}

function getPlatformName(pathname: string) {
  if (pathname.startsWith('/marketing-studio')) return 'Marketing Studio';
  if (pathname.startsWith('/business-core')) return 'Business Core';
  if (pathname.startsWith('/seller')) return 'Seller';
  if (pathname.startsWith('/flow')) return 'Flow';
  return 'CRM';
}

interface GlobalTopBarProps {
  onMenuClick?: () => void;
  showMenuButton?: boolean;
}

export function GlobalTopBar({ onMenuClick, showMenuButton }: GlobalTopBarProps) {
  const location = useLocation();
  const { user, logout, logoutAll, sessions, activeSessionId, switchSession, removeSession } = useAuth();
  const { tenants, currentTenant, loading: tenantsLoading, switchTenant } = useTenants();
  const { totalCount, countPerTenant } = useChatNotifications();
  const { connections: extensionConnections, overall: extensionOverall, checkHealth, checking } = useMarketplaceConnections();
  const showCredits = !!user && user.role !== 'SUPERADMIN';
  const { credits, loading: creditsLoading } = useCredits({ enabled: showCredits });
  const [accountOpen, setAccountOpen] = useState(false);
  const [tenantOpen, setTenantOpen] = useState(false);
  const [connectionsOpen, setConnectionsOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const tenantRef = useRef<HTMLDivElement>(null);
  const connectionsRef = useRef<HTMLDivElement>(null);
  const platformName = getPlatformName(location.pathname);
  const accountLabel = user?.nome || '';
  const extensionIndicator = describeExtensionIndicator(extensionConnections, extensionOverall);

  // Notificacoes so aparecem fora das plataformas de marketing/business.
  const isCrmArea = !location.pathname.startsWith('/marketing-studio') && !location.pathname.startsWith('/business-core');
  const visibleTotal = isCrmArea ? totalCount : 0;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (accountRef.current && !accountRef.current.contains(event.target as Node)) setAccountOpen(false);
      if (tenantRef.current && !tenantRef.current.contains(event.target as Node)) setTenantOpen(false);
      if (connectionsRef.current && !connectionsRef.current.contains(event.target as Node)) setConnectionsOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addAccount = () => {
    setAccountOpen(false);
    window.location.href = '/login?addAccount=1';
  };

  const handleTenantSwitch = async (tenantId: string) => {
    if (tenantId === currentTenant?.id) return;
    setTenantOpen(false);
    await switchTenant(tenantId);
  };

  return (
    <header className="fixed left-0 right-0 top-0 z-[1200] flex h-11 items-center justify-between border-b border-slate-200 bg-white/95 px-4 shadow-sm backdrop-blur">
      <div className="flex min-w-0 items-center gap-3">
        {showMenuButton && (
          <button
            type="button"
            onClick={onMenuClick}
            className="-ml-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-600 transition hover:bg-slate-100 lg:hidden"
            aria-label="Abrir menu"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-950 text-xs font-black text-white">
          IA
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-800">{platformName}</p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {/* Marketplace Connections Indicator.
            Verde so quando TODOS os acessos de TODAS as extensoes responderam ok.
            Ambar com "!" quando ha extensao conectada mas algum acesso (chat, metricas,
            afiliados...) esta indisponivel ou ainda nao foi verificado. */}
        <div ref={connectionsRef} className="relative">
          <button
            type="button"
            onClick={() => setConnectionsOpen((open) => !open)}
            className={`flex h-8 items-center gap-2 rounded-full border px-3 text-xs font-medium shadow-sm transition ${extensionIndicator.buttonClass}`}
            title={extensionIndicator.title}
          >
            {extensionIndicator.tone === 'warn' ? (
              <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold leading-none text-white" aria-hidden="true">!</span>
            ) : (
              <span className={`h-2 w-2 rounded-full ${extensionIndicator.dotClass}`} />
            )}
            <span className="hidden sm:block">{extensionIndicator.label}</span>
            <svg className={`h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform ${connectionsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {connectionsOpen && (
            <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-1rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
              <div className="border-b border-slate-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Extensões conectadas
              </div>
              <div className="max-h-96 overflow-y-auto py-1">
                {extensionConnections.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-slate-500">Nenhuma extensão conectada no momento</div>
                ) : (
                  extensionConnections.map((conn) => {
                    const health = conn.health;
                    const tone = healthTone(health.state);
                    const failing = health.checks.filter((c) => !c.ok);
                    return (
                      <div key={`${conn.marketplace}:${conn.connectionId}`} className="border-b border-slate-50 px-4 py-2.5 text-sm last:border-b-0">
                        <div className="flex items-center gap-3">
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100">
                            <PlatformIcon platform={conn.marketplace} size={18} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium text-slate-800">{conn.connectionName || conn.providerAccountId || 'Extensão'}</span>
                            <span className="block text-xs text-slate-500">
                              {conn.marketplace === 'mercadolivre' ? 'Mercado Livre' : 'Shopee'}
                              {conn.version ? ` · v${conn.version}` : ''}
                              {health.total > 0 ? ` · ${health.ok}/${health.total} acessos ok` : ''}
                            </span>
                          </span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); checkHealth(conn.connectionId, conn.marketplace); }}
                            disabled={checking === conn.connectionId}
                            className="shrink-0 rounded-md border border-slate-200 px-2 py-1 text-[10px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                          >
                            {checking === conn.connectionId ? '...' : 'Verificar'}
                          </button>
                          {tone === 'ok' ? (
                            <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                          ) : (
                            <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold leading-none text-white ${tone === 'error' ? 'bg-red-500' : 'bg-amber-500'}`} aria-hidden="true">!</span>
                          )}
                        </div>

                        {health.state === 'ok' && (
                          <p className="mt-1.5 pl-10 text-[11px] text-emerald-700">
                            Todos os acessos funcionando{health.checkedAt ? ` · verificado ${formatCheckedAt(health.checkedAt)}` : ''}.
                          </p>
                        )}

                        {health.state !== 'ok' && (
                          <div className="mt-1.5 space-y-1 pl-10">
                            {health.state === 'outdated' && (
                              <p className="text-[11px] font-medium text-amber-700">Extensão antiga: não consegue verificar os acessos.</p>
                            )}
                            {health.state === 'unknown' && (
                              <p className="text-[11px] font-medium text-amber-700">Conectada, aguardando verificação dos acessos...</p>
                            )}
                            {health.state === 'logged_out' && (
                              <p className="text-[11px] font-medium text-red-700">Conta deslogada no navegador.</p>
                            )}
                            {failing.map((c) => (
                              <p key={c.key} className="text-[11px] text-slate-600">
                                <span className="font-semibold text-red-600">✗ {c.label}:</span> {c.message}
                              </p>
                            ))}
                            {health.checks.filter((c) => c.ok).length > 0 && (
                              <p className="text-[11px] text-slate-500">
                                ✓ {health.checks.filter((c) => c.ok).map((c) => c.label).join(', ')}
                              </p>
                            )}
                            {health.fixHint && (
                              <p className="rounded-md bg-amber-50 px-2 py-1 text-[11px] text-amber-800">{health.fixHint}</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Saldo de créditos — antes vivia só no header de página do CRM; aqui aparece
            em todos os módulos. SUPERADMIN não consome créditos do tenant, então não vê. */}
        {showCredits && (
          <div
            className="flex h-8 items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 text-xs font-semibold text-amber-700 shadow-sm"
            title="Créditos disponíveis"
          >
            <svg className="h-4 w-4 shrink-0 text-amber-500" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.736 6.979C9.208 6.193 9.696 6 10 6c.304 0 .792.193 1.264.979a1 1 0 001.715-1.029C12.279 4.784 11.232 4 10 4s-2.279.784-2.979 1.95c-.285.475-.507 1-.67 1.55H6a1 1 0 000 2h.013a9.358 9.358 0 000 1H6a1 1 0 100 2h.351c.163.55.385 1.075.67 1.55C7.721 15.216 8.768 16 10 16s2.279-.784 2.979-1.95a1 1 0 10-1.715-1.029c-.472.786-.96.979-1.264.979-.304 0-.792-.193-1.264-.979a5.38 5.38 0 01-.491-.971H10a1 1 0 100-2H8.003a7.364 7.364 0 010-1H10a1 1 0 100-2H8.245c.155-.339.328-.654.491-.971z" />
            </svg>
            {creditsLoading ? (
              <span className="h-3.5 w-10 animate-pulse rounded bg-amber-200" />
            ) : (
              <span className="whitespace-nowrap">
                <span className="hidden md:inline">Créditos restantes: </span>
                {(credits?.balance ?? 0).toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            )}
          </div>
        )}

        {tenants.length > 0 && (
          <div ref={tenantRef} className="relative">
            <button
              type="button"
              onClick={() => setTenantOpen((open) => !open)}
              disabled={tenantsLoading}
              className="flex h-8 max-w-64 items-center gap-2 rounded-full border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60"
              title={currentTenant?.name || 'Selecionar empresa'}
            >
              <svg className="h-4 w-4 shrink-0 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8v-3a1 1 0 011-1h2a1 1 0 011 1v3" />
              </svg>
              <span className="hidden truncate sm:block">{currentTenant?.name || 'Empresa'}</span>
              {visibleTotal > 0 && (
                <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white">
                  {visibleTotal > 99 ? '99+' : visibleTotal}
                </span>
              )}
              <svg className={`h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform ${tenantOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {tenantOpen && (
              <div className="absolute right-0 mt-2 w-72 max-w-[calc(100vw-1rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
                <div className="border-b border-slate-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Empresas
                </div>
                <div className="max-h-72 overflow-y-auto py-1">
                  {tenants.map((tenant) => {
                    const active = tenant.id === currentTenant?.id || tenant.current;
                    return (
                      <button
                        key={tenant.id}
                        type="button"
                        onClick={() => handleTenantSwitch(tenant.id)}
                        disabled={tenantsLoading || active}
                        className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition ${active ? 'bg-slate-50 text-slate-900' : 'text-slate-700 hover:bg-slate-50'} disabled:cursor-default`}
                      >
                        <span className="min-w-0">
                          <span className="block truncate font-medium">{tenant.name}</span>
                          <span className="block truncate text-xs text-slate-500">{tenant.slug}</span>
                        </span>
                        <span className="flex shrink-0 items-center gap-2">
                          {isCrmArea && (countPerTenant[tenant.id] || 0) > 0 && (
                            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold leading-none text-white">
                              {countPerTenant[tenant.id] > 99 ? '99+' : countPerTenant[tenant.id]}
                            </span>
                          )}
                          {active && <span className="h-2 w-2 rounded-full bg-emerald-500" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        <div ref={accountRef} className="relative">
          <button
            type="button"
            onClick={() => setAccountOpen((open) => !open)}
            className="relative flex h-8 max-w-80 items-center gap-2 rounded-full border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 shadow-sm transition hover:bg-slate-50"
            title={accountLabel || 'Conta'}
          >
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-950 text-[10px] font-bold text-white">
              {user?.nome?.charAt(0)?.toUpperCase() || 'U'}
            </span>
            {accountLabel && <span className="hidden truncate sm:block">{accountLabel}</span>}
          </button>

          {accountOpen && (
            <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-1rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
              <div className="border-b border-slate-100 px-4 py-3">
                <p className="truncate text-sm font-semibold text-slate-900">{user?.nome}</p>
                <p className="truncate text-xs text-slate-500">{user?.email}</p>
                {currentTenant?.name && <p className="mt-1 truncate text-xs font-medium text-slate-600">{currentTenant.name}</p>}
              </div>

              <div className="max-h-64 overflow-y-auto py-1">
                {sessions.map((session) => {
                  const active = session.id === activeSessionId;
                  const sessionLabel = session.user.nome;
                  return (
                    <div key={session.id} className={`flex items-center gap-2 px-3 py-2 ${active ? 'bg-slate-50' : ''}`}>
                      <button
                        type="button"
                        onClick={() => !active && switchSession(session.id)}
                        className="min-w-0 flex-1 text-left"
                      >
                        <div className="flex items-center gap-1.5">
                          <p className="truncate text-sm font-medium text-slate-800">{sessionLabel}</p>
                        </div>
                        <p className="truncate text-xs text-slate-500">{session.user.email}</p>
                      </button>
                      {active && (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Atual</span>
                      )}
                      {!active && (
                        <button
                          type="button"
                          onClick={() => removeSession(session.id)}
                          className="rounded-md px-2 py-1 text-xs text-red-500 hover:bg-red-50"
                        >
                          Remover
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-slate-100 p-2">
                <button type="button" onClick={addAccount} className="w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50">
                  Adicionar outra conta
                </button>
                <button type="button" onClick={logout} className="w-full rounded-lg px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50">
                  Sair desta conta
                </button>
                {sessions.length > 1 && (
                  <button type="button" onClick={logoutAll} className="w-full rounded-lg px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50">
                    Sair de todas as contas
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        <PlatformSwitcher compact />
      </div>
    </header>
  );
}
